import { useMemo } from 'react';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import { StepType, ConditionType, LogicType } from '@de/types/enums';
import type { MultiQuestion, Step } from '@de/types/types';

const MAX_PROGRESS = 95;
const BASE_PROGRESS = 10;
const PROGRESS_SPAN = MAX_PROGRESS - BASE_PROGRESS;

// Helper to check if a step is a countable question
const isCountableQuestion = (step: Step | undefined) => {
  return (
    step?.type === StepType.QUESTION || step?.type === StepType.MULTI_QUESTION
  );
};

// Helper to estimate the total number of questions in the default path
const estimateDefaultPathLength = (steps: Step[]): number => {
  if (!steps || steps.length === 0) return 5;

  const childQuestionIds = new Set<string>();
  for (const s of steps) {
    if (s.type === StepType.MULTI_QUESTION) {
      const mq = s as Step & MultiQuestion;
      mq.stepQuestions?.forEach((q) => q.id && childQuestionIds.add(q.id));
    }
  }

  const stepMap = new Map<string, Step>();
  for (const s of steps) if (s.id) stepMap.set(s.id, s);

  const entry = steps.find((s) => s.isEntry) || steps[0];
  let count = 0;
  const visited = new Set<string>();
  let curr = entry?.id;

  while (curr && !visited.has(curr)) {
    visited.add(curr);
    const step = stepMap.get(curr);
    if (!step) break;

    if (isCountableQuestion(step) && !childQuestionIds.has(step.id || ''))
      count++;
    if (step.isTerminal) break;

    const defaultBranch = step.branchingLogic?.find(
      (b) => b.condition?.type === ConditionType.DEFAULT,
    );
    if (!defaultBranch || defaultBranch.type === LogicType.RESULT) break;

    curr = defaultBranch.nextId;
  }

  // Floor to a reasonable minimum so short flows still feel progressive
  return Math.max(count, 5);
};

/**
 * Computes branch-aware flow progress as a percentage (10–95).
 *
 * It calculates an estimated total number of questions based on the default path.
 * Then it counts the actual visited questions from the navigation history.
 * Progress is then: BASE_PROGRESS + (visited / estimatedTotal) * PROGRESS_SPAN
 */
export function useFlowProgress(): number {
  const { steps, currentStep, navigationHistory } =
    useDynamicExperienceContext();

  const estimatedTotal = useMemo(
    () => estimateDefaultPathLength(steps),
    [steps],
  );

  const progress = useMemo(() => {
    if (!currentStep?.id || steps.length === 0) return 0;

    const childQuestionIds = new Set<string>();
    for (const s of steps) {
      if (s.type === StepType.MULTI_QUESTION) {
        const mq = s as Step & MultiQuestion;
        mq.stepQuestions?.forEach((q) => q.id && childQuestionIds.add(q.id));
      }
    }

    // Filter navigation history to only count actual questions
    const visitedQuestions = navigationHistory.filter(
      (step) =>
        isCountableQuestion(step) && !childQuestionIds.has(step.id || ''),
    );
    const visitedCount = visitedQuestions.length;

    const calculated = Math.min(
      BASE_PROGRESS + (visitedCount / estimatedTotal) * PROGRESS_SPAN,
      MAX_PROGRESS,
    );

    return Math.round(calculated);
  }, [steps, currentStep?.id, navigationHistory, estimatedTotal]);

  return progress;
}
